import React from 'react';

import { Choice, StateResponse } from '../../api/api';
import { GeneralError } from '../commons/components/general-error';
import { useGlobalStateUpdate, useSessionState } from '../global-state';
import { SelectedChoiceLostModal } from './category-lost-modal';
import { sessionIncludesChoice } from './check-category-lost';
import { useUserChoiceMutation } from './user-choice';

export interface UserChoiceProviderChildProps {
  onUpdate: (newChoice: Choice) => void;
}

interface UserChoiceProviderProps {
  children: (props: UserChoiceProviderChildProps) => React.ReactNode;
}

export const UserChoiceProvider: React.FC<UserChoiceProviderProps> = ({ children }) => {
  const updateGlobalState = useGlobalStateUpdate();
  const session = useSessionState();

  const [categoryLost, setCategoryLost] = React.useState<boolean>(false);

  const userChoiceMutation = useUserChoiceMutation();

  const callUpdateChoice = (newChoice: Choice) => {
    userChoiceMutation.mutate(newChoice, {
      onSuccess: (newState: StateResponse) => {
        const newSession = newState.sessions.find(
          ({ session_id }) => session_id === session.session_id
        );
        /**
         * It is not so relevant whether the requested choice becomes the user
         * choice, because it may not happen so for multiple reasons like for
         * example concurrent updates. In such situation we just reset the
         * selection to whatever is selected on backend. For showing the modal
         * it is only important if the choice is still available or not.
         */
        if (sessionIncludesChoice(newSession!, newChoice)) {
          updateGlobalState(newState);
        } else {
          setCategoryLost(true);
        }
      },
    });
  };

  const childProps: UserChoiceProviderChildProps = {
    onUpdate: callUpdateChoice,
  };

  return (
    <>
      {children(childProps)}
      <GeneralError error={userChoiceMutation.error} onClose={userChoiceMutation.reset} />
      {categoryLost && (
        <SelectedChoiceLostModal
          onClose={() => {
            setCategoryLost(false);
            updateGlobalState(userChoiceMutation.data!);
          }}
        />
      )}
    </>
  );
};
